import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useAppDispatch } from '@app/hooks/reduxHooks'
import { doLogin } from '@app/store/slices/auth-slice'
import { Button, Input } from '@asad/ui'
import { Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react'
import logo from '@app/assets/img/fud-finance-logo.png'
import darkLogo from '@app/assets/img/fud-finance-dark-logo.png.png'
import loginBackground from '@app/assets/img/login_background.png'
import { notificationController } from '@app/controllers/notification-controller'

import LangueSelector from '@/components/common/languageSelector/LangueSelector'
import { updateTheme } from '@/utils/utils'

const loginSchema = z.object({
  email: z.string().trim().email('loginPage.emailError'),
  password: z.string().trim().min(1, 'loginPage.passwordRequired'),
})

type LoginFormValues = z.infer<typeof loginSchema>

export const LoginForm = () => {
  const [showPassword, setShowPassword] = React.useState(false)

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
  })

  const storedTheme = localStorage.getItem('theme') || 'dark'

  const translateLoginError = (message?: string) => {
    if (!message) return t('loginPage.genericLoginError')

    const translatedMessage = t(message)
    if (translatedMessage && translatedMessage !== message) return translatedMessage

    const lowered = message.toLowerCase()
    if (lowered.includes('block')) return t('loginPage.userBlocked')
    if (lowered.includes('credential') || lowered.includes('password') || lowered.includes('unauthorized')) {
      return t('loginPage.incorrectCredentials')
    }

    return t('loginPage.genericLoginError')
  }

  const onSubmit = async (values: LoginFormValues) => {
    try {
      await dispatch(doLogin(values)).unwrap()
      notificationController.success({ message: t('beNotifications.success.loginSuccess') })
      navigate('/', { replace: true })
    } catch (error) {
      const message = translateLoginError(error as string)
      notificationController.error({ message })
    }
  }

  React.useEffect(() => {
    updateTheme(storedTheme === 'light')
  }, [storedTheme])

  return (
    <div
      className='relative h-screen w-screen overflow-hidden bg-cover bg-center'
      style={{ backgroundImage: `url(${loginBackground})` }}
    >
      <div className='absolute inset-0 z-0'>
        <div className='absolute inset-0 bg-black/60 backdrop-blur-sm' />
        <div className='absolute inset-0 bg-login-vignette pointer-events-none' />
      </div>
      <div className='relative z-10 flex flex-col min-h-screen'>
        <header className='w-full px-4 pt-4 flex flex-col sm:flex-row items-center sm:justify-between gap-2'>
          <img
            className='h-20 sm:h-28 drop-shadow-lg mx-auto sm:mx-0'
            src={storedTheme === 'dark' ? logo : darkLogo}
            alt='FUD'
          />
          <div className='sm:mt-2 flex justify-center sm:justify-end'>
            <LangueSelector />
          </div>
        </header>
        <main className='flex-1 flex items-center justify-center px-4'>
          <div className='w-full max-w-lg'>
            <div className='w-full rounded-2xl bg-black/55 shadow-2xl sm:px-10 sm:py-10 p-4 border border-white/20'>
              <div className='font-bold text-2xl sm:text-3xl mb-2 text-center text-white'>{t('loginPage.title')}</div>
              <div className='text-sm mb-4 sm:mb-8 text-center text-slate-200'>{t('loginPage.credentialsPrompt')}</div>
              <form className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)} noValidate>
                <div
                  className={`flex items-center gap-3 h-12 rounded-lg border px-4 bg-black/30 transition-colors ${
                    errors.email
                      ? 'border-error focus-within:border-error'
                      : 'border-white/15 focus-within:border-primary'
                  }`}
                >
                  <Mail className='w-5 h-5 text-slate-200' />
                  <Input
                    placeholder={t('loginPage.emailPlaceholder')}
                    className='bg-transparent border-none text-white placeholder:text-slate-300 h-full px-0 mb-0'
                    autoComplete='email'
                    {...register('email')}
                    type='email'
                    error={!!errors.email}
                  />
                </div>
                {errors.email?.message && (
                  <div className='text-error text-xs font-normal -mt-2'>{t(errors.email.message)}</div>
                )}

                <div
                  className={`flex items-center gap-3 h-12 rounded-lg border px-4 bg-black/30 transition-colors ${
                    errors.password
                      ? 'border-error focus-within:border-error'
                      : 'border-white/15 focus-within:border-primary'
                  }`}
                >
                  <Lock className='w-5 h-5 text-slate-200' />
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    placeholder={t('loginPage.passwordPlaceholder')}
                    className='bg-transparent border-none text-white placeholder:text-slate-300 h-full px-0 mb-0'
                    {...register('password')}
                    error={!!errors.password}
                  />
                  <button
                    type='button'
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className='text-slate-200 hover:text-white transition-colors'
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <EyeOff className='w-5 h-5' /> : <Eye className='w-5 h-5' />}
                  </button>
                </div>
                {errors.password?.message && (
                  <div className='text-error text-xs font-normal -mt-2'>{t(errors.password.message)}</div>
                )}
                <Button size='lgFull' className='h-12 text-base justify-center' type='submit' disabled={isSubmitting}>
                  {t('loginPage.login')}
                </Button>
              </form>
            </div>
          </div>
        </main>
        <footer className='w-full p-4 bg-black/55 backdrop-blur-sm text-slate-200 text-base flex flex-wrap flex-col sm:flex-row items-center justify-between gap-6'>
          <div className='flex sm:items-center gap-3 flex-wrap flex-col sm:flex-row'>
            <a href='mailto:biuro@fudfinance.pl' className='text-blue-400 transition-colors flex gap-2 items-center'>
              <Mail className='w-4 h-4' /> biuro@fudfinance.pl
            </a>
            <span className='mx-1 hidden sm:inline'>|</span>
            <a href='tel:+48792423161' className='text-blue-400 transition-colors flex gap-2 items-center'>
              <Phone className='w-4 h-4' /> +48 792 423 161
            </a>
          </div>
          <div className='text-slate-300'>{`© ${new Date().getFullYear()} FUD Finance S.A.`}</div>
        </footer>
      </div>
    </div>
  )
}
